import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Business from '@mui/icons-material/Business';
import AccountTree from '@mui/icons-material/AccountTree';
import Layers from '@mui/icons-material/Layers';
import FolderShared from '@mui/icons-material/FolderShared';
import ContentCopy from '@mui/icons-material/ContentCopy';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import { useTranslation } from 'react-i18next';
import { client, MarketCategory, ReferenceInstance } from '../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import { DetailPageTemplate } from '../../../components/page-templates/detail-page/DetailPageTemplate';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import { TableSection, TableColumn } from '../../../components/shared/tables/TableSection';
import type { TabDefinition } from '../../../components/shared/tabs/TabbedSection';
import { getCategoryIcon } from '../../Storefront/components/CategorySidebar';
import { MATERIAL_ICON_OPTIONS, resolveMaterialIcon } from '../../orderly-for-distributors/ItemTags/components/itemTagUtils';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const CategoryForm = () => {
  const { t } = useTranslation('categories');
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const isEdit = Boolean(categoryId);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { instances } = useInstanceMarket();
  const [availableCategories, setAvailableCategories] = useState<MarketCategory[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const companyId = searchParams.get('companyId');
  const searchTerm = searchParams.get('searchTerm');

  const [formData, setFormData] = useState<MarketCategory>({
    categoryId: 0,
    parentId: -2,
    parentName: '',
    name: '',
    companyId: 0,
    instanceName: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const fetchCategoriesForInstance = async (selectedCompanyId: number) => {
    if (!selectedCompanyId) {
      setAvailableCategories([]);
      return;
    }

    try {
      const result = await client.getMarketCategories({
        companyId: selectedCompanyId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
        sort: '',
        direction: 'asc',
      });

      if (result.success && result.data) {
        const categories = result.data.items.filter((cat) => !isEdit || cat.categoryId !== parseInt(categoryId!, 10));
        setAvailableCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setAvailableCategories([]);
    }
  };

  const fetchCategory = async () => {
    if (!categoryId) return;

    setIsLoading(true);
    try {
      const result = await client.getMarketCategory(categoryId);
      if (result.success && result.data) {
        setFormData(result.data);
        await fetchCategoriesForInstance(result.data.companyId);
      }
    } catch (error) {
      console.error('Error fetching category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchCategory();
    }
  }, [categoryId, isEdit]);

  const [subCategoriesPage, setSubCategoriesPage] = useState<number>(0);
  const SUB_CATEGORIES_PAGE_SIZE = 10;

  const subCategories = isEdit && categoryId
    ? availableCategories.filter((category) => category.parentId === parseInt(categoryId, 10))
    : [];
  const subCategoryCount = subCategories.length;

  const sameNameCategoriesCount = useMemo(() => {
    if (!formData.companyId) return 0;

    const normalizedName = formData.name.trim().toLowerCase();
    if (!normalizedName) return 0;

    return availableCategories.filter((category) => category.name?.trim().toLowerCase() === normalizedName).length;
  }, [availableCategories, formData.companyId, formData.name]);

  const siblingCategoriesCount = useMemo(() => {
    if (!formData.companyId) return 0;

    if (formData.parentId <= 0) return 0;

    return availableCategories.filter((category) => category.parentId === formData.parentId).length;
  }, [availableCategories, formData.companyId, formData.parentId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('categoryNameRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketCategory) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: ReferenceInstance | null) => {
    const newCompanyId = value?.id || 0;
    setFormData((prev) => ({
      ...prev,
      companyId: newCompanyId,
      parentId: -2,
    }));

    if (newCompanyId) {
      fetchCategoriesForInstance(newCompanyId);
    } else {
      setAvailableCategories([]);
    }

    if (errors.companyId) {
      setErrors((prev) => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleParentCategoryChange = (event: React.SyntheticEvent, value: MarketCategory | null) => {
    setFormData((prev) => ({
      ...prev,
      parentId: value?.categoryId || -2,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketCategory(categoryId!, formData);
        if (result.success) {
          setMessage(t('procurementUpdateSuccessMessage', 'Procurement category updated successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/categories${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        }
      } else {
        const result = await client.createMarketCategory(formData);
        if (result.success) {
          setMessage(t('procurementCreateSuccessMessage', 'Procurement category created successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/categories${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setMessage(isEdit
        ? t('procurementUpdateErrorMessage', 'Failed to update procurement category')
        : t('procurementCreateErrorMessage', 'Failed to create procurement category'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/procurement/categories${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'CategoryUpdate' : 'CategoryCreate', 'ConfigurationAdmin']);

  const getSelectedParentCategory = () => {
    if (formData.parentId <= 0) return null;
    return availableCategories.find((cat) => cat.categoryId === formData.parentId) || null;
  };

  const instanceName = instances.find((inst) => inst.id === formData.companyId)?.name ?? '—';
  const CategoryPictureIcon = formData.iconKey ? resolveMaterialIcon(formData.iconKey) : CategoryOutlined;

  const categoryPicture = (
    <Section style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 5,
    }}
    >
      <CategoryPictureIcon sx={{ fontSize: 96, color: formData.iconKey ? 'primary.main' : 'text.disabled' }} />
    </Section>
  );

  const handleSubCategoryRowClick = (category: MarketCategory) => {
    navigate(`/procurement/categories/edit/${category.categoryId}?companyId=${category.companyId}`);
  };

  const subCategoryColumns: TableColumn<MarketCategory>[] = [
    { id: 'name', label: t('categoryName'), field: 'name' },
    { id: 'icon', label: t('icon'), render: (row) => getCategoryIcon(row.iconKey) },
    { id: 'instanceName', label: t('instanceName'), field: 'instanceName' },
  ];

  const subCategoriesTabContent = (
    <TableSection<MarketCategory>
      title={t('procurementTileSubCategories', 'Sub-Categories')}
      columns={subCategoryColumns}
      rows={subCategories.slice(subCategoriesPage * SUB_CATEGORIES_PAGE_SIZE, (subCategoriesPage + 1) * SUB_CATEGORIES_PAGE_SIZE)}
      getRowId={(row) => `${row.categoryId}-${row.companyId}`}
      onRowClick={handleSubCategoryRowClick}
      recordCount={subCategories.length}
      recordCountLabel={t('procurementTileSubCategories', 'Sub-Categories')}
      page={subCategoriesPage}
      maxPages={Math.max(1, Math.ceil(subCategories.length / SUB_CATEGORIES_PAGE_SIZE))}
      onPageChange={setSubCategoriesPage}
    />
  );

  const overviewTabContent = (
    <Grid
      container
      spacing={4}
    >
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextField
          fullWidth
          label={t('categoryName')}
          placeholder={t('categoryNamePlaceholder')}
          value={formData.name}
          onChange={handleInputChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          required
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <FormControl fullWidth>
          <InputLabel shrink>{t('categoryIcon')}</InputLabel>
          <Select
            value={formData.iconKey ?? ''}
            displayEmpty
            label={t('categoryIcon')}
            onChange={(e) => setFormData((prev) => ({ ...prev, iconKey: e.target.value }))}
            sx={{
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                minHeight: '1.4375em !important',
              },
            }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography
                      variant="textSM"
                      sx={{ lineHeight: 1.2 }}
                    >
                      {t('noIcon')}
                    </Typography>
                  </Box>
                );
              }

              const option = MATERIAL_ICON_OPTIONS.find((opt) => opt.key === value);
              const IconComponent = resolveMaterialIcon(value as string);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconComponent sx={{ fontSize: 20 }} />
                  <Typography variant="textSM">{option?.label ?? value}</Typography>
                </Box>
              );
            }}
          >
            <MenuItem value="">
              <Typography variant="textSM">{t('noIcon')}</Typography>
            </MenuItem>
            {MATERIAL_ICON_OPTIONS.map(({ key, label, Icon }) => (
              <MenuItem
                key={key}
                value={key}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon sx={{ fontSize: 20 }} />
                  <Typography variant="textSM">{label}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <Autocomplete
          options={instances}
          getOptionLabel={(option) => option.name}
          value={instances.find((inst) => inst.id === formData.companyId) || null}
          onChange={handleInstanceChange}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('instance')}
              placeholder={t('instancePlaceholder')}
              error={!!errors.companyId}
              helperText={errors.companyId}
              required
            />
          )}
          noOptionsText={t('noInstancesFound')}
          clearOnBlur
          selectOnFocus
          handleHomeEndKeys
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <Autocomplete
          options={availableCategories}
          getOptionLabel={(option) => option.name}
          value={getSelectedParentCategory()}
          onChange={handleParentCategoryChange}
          isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('parentCategory')}
              placeholder={t('parentCategoryPlaceholder')}
            />
          )}
          noOptionsText={t('noCategoriesFoundForSelection')}
          clearOnBlur
          selectOnFocus
          handleHomeEndKeys
        />
      </Grid>
    </Grid>
  );

  const tabs: TabDefinition[] = [
    {
      id: 'overview',
      label: t('overview', 'Overview'),
      content: overviewTabContent,
    },
    {
      id: 'subCategories',
      label: t('procurementTileSubCategories', 'Sub-Categories'),
      content: subCategoriesTabContent,
      disabled: !isEdit,
    },
  ];

  return (
    <DetailPageTemplate
      title={t(isEdit ? 'procurementEditCategory' : 'procurementCreateCategory', isEdit ? 'Edit Procurement Category' : 'Create Procurement Category')}
      subtitle={t('procurementCategoryFormSubtitle', 'Manage procurement category setup, hierarchy, and icon.')}
      loading={isLoading}
      loadingLabel={t('loading')}
      actions={(
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
          >
            {t('cancel')}
          </Button>
          {userCanSave && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
            </Button>
          )}
        </>
      )}
      headlineContent={{
        layout: 'picture-six',
        picture: categoryPicture,
        insights: [
          <InsightTile
            key="instance"
            label={t('instance', 'Instance')}
            value={formData.companyId ? instanceName : t('procurementTileNotSet', 'Not set')}
            description={t('procurementTileInstanceDescription', 'Instance this category belongs to')}
            icon={<Business />}
            variant="primary"
          />,
          <InsightTile
            key="parentCategory"
            label={t('parentCategory', 'Parent Category')}
            value={formData.parentId > 0 ? formData.parentName || getSelectedParentCategory()?.name || t('none') : t('none')}
            description={t('procurementTileParentCategoryDescription', 'Category this one is nested under, if any')}
            icon={<AccountTree />}
            variant="secondary"
          />,
          <InsightTile
            key="sameNameCategories"
            label={t('procurementTileDuplicateNames', 'Possible Duplicate Names')}
            value={formData.companyId ? sameNameCategoriesCount : t('procurementTileNotSet', 'Not set')}
            description={t('procurementTileDuplicateNamesDescription', 'Categories sharing the same name as another category')}
            icon={<ContentCopy />}
            variant="primary"
          />,
          <InsightTile
            key="categoryLevel"
            label={t('procurementTileCategoryLevelLabel', 'Category Level')}
            value={formData.parentId > 0
              ? t('procurementTileCategoryLevelSubCategory', 'Sub-Category')
              : t('procurementTileCategoryLevelTopLevel', 'Top-Level')}
            description={t('procurementTileCategoryLevelDescription', 'Where this category sits in the hierarchy')}
            icon={<Layers />}
            variant="secondary"
          />,
          <InsightTile
            key="subCategories"
            label={t('procurementTileSubCategories', 'Sub-Categories')}
            value={isEdit ? subCategoryCount : t('procurementTileNotSet', 'Not set')}
            description={t('procurementTileSubCategoriesOfThisDescription', 'Categories nested directly under this one')}
            icon={<FolderShared />}
            variant="default"
          />,
          <InsightTile
            key="siblingCategories"
            label={t('procurementTileSiblingCategoriesLabel', 'Sibling Categories')}
            value={formData.companyId && formData.parentId > 0 ? siblingCategoriesCount : t('procurementTileNotSet', 'Not set')}
            description={t('procurementTileSiblingCategoriesDescription', 'Categories sharing the same parent')}
            icon={<FolderShared />}
            variant="success"
          />,
        ],
      }}
      tabs={tabs}
      tabsAriaLabel={t('categoryName')}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </DetailPageTemplate>
  );
};

export default CategoryForm;
